import 'bootstrap/dist/css/bootstrap.min.css'
import React from 'react'
import { GeoJSON, Marker, Tooltip } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-markercluster/dist/index.js'
import { BpmMap } from './BpmMap'
import * as Constants from './constants'
import { STATES_DATA } from './states-data'
import { getRatioAsPercentage } from './utils'
import { WinLossDecisionLocationsTable } from './WinLossDecisionLocationsTable'
import { WinLossUSRegionsTable } from './WinLossUSRegionsTable'

export const WinLossMap = ({
    rawData,
    sectionNumber,
    productType,
    year,
    startMonth,
    endMonth,
    mapType,
}) => {
    const noDataFound = (
        <p>No Win/Loss data found for selected section and year.</p>
    )
    if (!rawData || rawData.length === 0) {
        return noDataFound
    }

    // Filter by selected year, months, and product type
    const filteredData = rawData.filter(
        (d) =>
            d.year === year &&
            d.month >= startMonth &&
            d.month <= endMonth &&
            (!productType ||
                productType === Constants.ALL_PRODUCT_TYPES ||
                d.productTypeName === productType)
    )

    const dataLookup =
        mapType === Constants.MAP_TYPE_US_REGIONS
            ? getRegionsToWinLoss(filteredData)
            : getlocationToWinLoss(filteredData)
    const mapContent =
        mapType === Constants.MAP_TYPE_US_REGIONS
            ? getStatesGeoJSON(
                  dataLookup,
                  sectionNumber,
                  productType,
                  year,
                  startMonth,
                  endMonth
              )
            : getCitiesMarkers(dataLookup)
    const dataTable =
        mapType === Constants.MAP_TYPE_US_REGIONS ? (
            <WinLossUSRegionsTable regionToWinLoss={dataLookup} />
        ) : (
            <WinLossDecisionLocationsTable locationToWinLoss={dataLookup} />
        )

    return <BpmMap mapContent={mapContent} dataTable={dataTable} />
}

function getRegionsToWinLoss(filteredData) {
    // Map each region to its win/loss data so we can color the states according to their wins.
    return filteredData.reduce((map, data) => {
        const region = data.region.toUpperCase()
        if (!region) {
            return map
        }

        const winLoss = map.get(region)
        map.set(region, {
            wins: winLoss ? winLoss.wins + data.wins : data.wins,
            losses: winLoss ? winLoss.losses + data.losses : data.losses,
        })
        return map
    }, new Map())
}

function getStatesGeoJSON(
    regionToWinLoss,
    sectionNumber,
    productType,
    year,
    startMonth,
    endMonth
) {
    // Because the tooltips bound to each feature (US state) are generated outside of React's component tree (using onEachFeature()), regenerate the component
    // each time the user's filter changes to make sure the tooltips are accurate.
    const filterKey = `${sectionNumber}::${productType}::${year}::${startMonth}::${endMonth}`
    return (
        <GeoJSON
            key={filterKey}
            data={STATES_DATA}
            style={(feature) => {
                return {
                    fillColor: getRegionFillColor(
                        feature.properties.name.toUpperCase(),
                        regionToWinLoss
                    ),
                    weight: 1,
                    opacity: 1,
                    color: 'white',
                    fillOpacity: 0.7,
                }
            }}
            onEachFeature={(feature, layer) => {
                if (feature && feature.properties && feature.properties.name) {
                    const winLoss = regionToWinLoss.get(
                        feature.properties.name.toUpperCase()
                    )
                    if (winLoss) {
                        const winPercentage = getRatioAsPercentage(
                            winLoss.wins,
                            winLoss.wins + winLoss.losses
                        )
                        if (winPercentage || winPercentage === 0) {
                            layer.bindTooltip(
                                `${feature.properties.name}: ${winLoss.wins} wins, ${winLoss.losses} losses (${winPercentage}% won)`
                            )
                        }
                    }
                }
            }}
        />
    )
}

function getRegionFillColor(regionName, regionToWinLoss) {
    const winPct = getRegionWinPercentage(regionName, regionToWinLoss)
    if (!winPct && winPct !== 0) {
        return 'transparent'
    } else if (winPct === 0) {
        return Constants.COLOR_0
    } else if (winPct <= 20) {
        return Constants.COLOR_1_OUT_OF_5
    } else if (winPct <= 40) {
        return Constants.COLOR_2_OUT_OF_5
    } else if (winPct <= 60) {
        return Constants.COLOR_3_OUT_OF_5
    } else if (winPct <= 80) {
        return Constants.COLOR_4_OUT_OF_5
    } else {
        return Constants.COLOR_5_OUT_OF_5
    }
}

function getRegionWinPercentage(regionName, regionToWinLoss) {
    const winLoss = regionToWinLoss.get(regionName)
    if (!winLoss) {
        return null
    }

    const totalDecisions = winLoss.wins + winLoss.losses
    if (!totalDecisions) {
        return null
    }

    return getRatioAsPercentage(winLoss.wins, winLoss.wins + winLoss.losses)
}

function getlocationToWinLoss(filteredData) {
    // Map each location to its total wins and losses so we can create a marker for each location
    return filteredData.reduce((map, data) => {
        if (
            !data ||
            data.latitude === undefined ||
            data.longitude === undefined
        ) {
            return map
        }

        const key = `${data.latitude},${data.longitude}`
        const locationWinLoss = map.get(key)
        map.set(key, {
            country: data.country,
            region: data.region,
            city: data.city,
            wins: locationWinLoss
                ? locationWinLoss.wins + data.wins
                : data.wins,
            losses: locationWinLoss
                ? locationWinLoss.losses + data.losses
                : data.losses,
        })
        return map
    }, new Map())
}

function getCitiesMarkers(locationToWinLoss) {
    let markers = []
    for (const [key, value] of locationToWinLoss) {
        const winPercentage = getRatioAsPercentage(
            value.wins,
            value.wins + value.losses
        )
        const icon =
            winPercentage < 40
                ? Constants.PIN_ICON_BAD
                : winPercentage < 60
                ? Constants.PIN_ICON_MIXED
                : Constants.PIN_ICON_GOOD
        markers.push(
            <Marker key={`marker-${key}`} position={key.split(',')} icon={icon}>
                <Tooltip
                    key={`tooltip-${key}`}
                >{`${value.city}: ${value.wins} wins, ${value.losses} losses (${winPercentage}% won)`}</Tooltip>
            </Marker>
        )
    }

    return <MarkerClusterGroup>{markers}</MarkerClusterGroup>
}
